/**
 * Helper function to replace dynamic placeholders with actual values
 * Supports two patterns:
 * 1. Simple: <<placeholder>> - calls config[placeholder]()
 * 2. Function: <<funcname(param)>> - calls config[funcname](param)
 *
 * @param text - Text containing placeholders
 * @param config - Configuration object with placeholder keys and functions
 * @returns Text with all placeholders replaced by actual values
 */
export const replacePlaceholders = (text: string, config: Record<string, (...args: any[]) => string>): string => {
  let result = text;

  // Match both <<simple>> and <<func(params)>> patterns
  const placeholderPattern = /<<([^>(]+)(?:\(([^)]*)\))?>>(?!\))/g;

  result = result.replace(placeholderPattern, (match, funcName, params) => {
    const func = config[funcName.trim()];
    if (!func) return match; // Keep original if function not found

    // If no params, call with no arguments
    if (params === undefined) {
      return func();
    }

    // Parse parameters (handles numbers, strings, booleans)
    const parsedParams = params.split(',').map((p: string) => {
      const trimmed = p.trim();
      // Remove quotes for strings
      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        return trimmed.slice(1, -1);
      }
      // Parse numbers
      if (!isNaN(Number(trimmed))) {
        return Number(trimmed);
      }
      // Parse booleans
      if (trimmed === 'true') return true;
      if (trimmed === 'false') return false;
      // Return as string
      return trimmed;
    });

    return func(...parsedParams);
  });

  return result;
};
